<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Document from '@tiptap/extension-document'
import { Token, tokenLabel } from '../tiptap/token_node.js'
import { contentToEditorHtml, editorHtmlToContent } from '../tiptap/token_serialize.js'

// One WYSIWYG editor for a single editable piece of the quote email: the main
// document (mode "block", main: true) or one dynamic-block sub-field. The stored
// format stays `{{token}}`-in-HTML.
//
// Override semantics mirror the backend's blank-means-default rule: the hidden
// input starts as the saved override (or "" when none), stays "" until the admin
// actually edits, and Reset puts the built-in default back in the editor while
// submitting "" (= delete the override row). `content` is null when no override
// is saved; the editor then shows `defaultContent`.
//
// mode "inline": the field's output is wrapped by code-owned markup (<li>…</li>,
// the callout <p>), so the editor is a single paragraph and the serialised value
// has the <p> wrapper stripped.
const props = defineProps({
  name:           { type: String, required: true },
  label:          { type: String, default: '' },
  content:        { type: String, default: null },
  defaultContent: { type: String, default: '' },
  requiredTokens: { type: Array, default: () => [] },
  allowedTokens:  { type: Array, default: () => [] },
  mode:           { type: String, default: 'block' },
  main:           { type: Boolean, default: false },
})

const emit = defineEmits(['change'])

const hidden   = ref(null)
const menuOpen = ref(false)

const inline = computed(() => props.mode === 'inline')
const overridden = ref(props.content != null && props.content !== '')

// Built here, not in the template: the literal "{{...}}" would otherwise collide
// with Vue's own mustache interpolation delimiters in the SFC parser.
const requiredHint = computed(() =>
  props.requiredTokens.map((t) => `{{${t}}}`).join(' ')
)

// Strip the single top-level <p> wrapper from an inline field's serialised
// value (the paragraph node is a TipTap structural requirement, not content).
function stripParagraph(html) {
  const body = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body
  if (body.children.length === 1 && body.firstElementChild.tagName === 'P') {
    return body.firstElementChild.innerHTML
  }
  return html
}

function serialise() {
  if (!editor.value) return ''
  const s = editorHtmlToContent(editor.value.getHTML())
  return inline.value ? stripParagraph(s) : s
}

function currentValue() {
  // An emptied editor (select-all + delete) means "use the default", same as
  // Reset — otherwise TipTap's structural <p></p> would save as an override
  // that renders an empty paragraph.
  if (!overridden.value || editor.value?.isEmpty) return ''
  return serialise()
}

function syncHidden() {
  if (hidden.value) hidden.value.value = currentValue()
}

function onUpdate() {
  overridden.value = true
  syncHidden()
  emit('change', props.name, currentValue())
}

const extensions = inline.value
  ? [
      // Single-paragraph schema: no headings/lists/blockquotes, no hard breaks —
      // the value must stay one line inside its code-owned wrapper.
      StarterKit.configure({
        document: false, heading: false, bulletList: false, orderedList: false,
        listItem: false, blockquote: false, codeBlock: false, horizontalRule: false,
        hardBreak: false, link: false,
      }),
      Document.extend({ content: 'paragraph' }),
      Token,
    ]
  : [
      StarterKit.configure({ link: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: null, target: null } }),
      Token,
    ]

const editor = useEditor({
  content: contentToEditorHtml(props.content ?? props.defaultContent),
  extensions,
  editorProps: {
    attributes: { class: 'email-prose focus:outline-none' },
  },
  onUpdate,
})

function is(name, attrs) { return editor.value?.isActive(name, attrs) }

function setLink() {
  const url = window.prompt('Link URL', 'https://')
  if (url === null) return
  if (url === '') { editor.value.chain().focus().unsetLink().run(); return }
  editor.value.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

function insertToken(name) {
  editor.value.chain().focus().insertContent({ type: 'token', attrs: { name } }).run()
  menuOpen.value = false
}

function reset() {
  if (!editor.value) return
  editor.value.commands.setContent(contentToEditorHtml(props.defaultContent), { emitUpdate: false })
  overridden.value = false
  syncHidden()
  emit('change', props.name, '')
}

function chipLabel(name) { return tokenLabel(name) }

// Seed the hidden input once on mount: the saved override verbatim (lossless
// until edited), or "" when the built-in default is in effect.
function onHiddenRef(el) {
  hidden.value = el
  syncHidden()
}

onBeforeUnmount(() => editor.value?.destroy())
</script>

<template>
  <div class="email-field-editor" :data-field="name">
    <input type="hidden" :name="`email_sections[${name}]`" :ref="onHiddenRef" />

    <div v-if="editor">
      <div class="flex flex-wrap items-center gap-1 mb-1">
        <span v-if="label" class="text-xs font-medium text-base-content/70 mr-2">
          {{ label }}
          <span v-if="requiredTokens.length" class="font-mono text-accent ml-1">
            requires {{ requiredHint }}
          </span>
          <span v-if="overridden" class="badge badge-ghost badge-xs ml-1">edited</span>
        </span>

        <button type="button" class="btn btn-xs" :class="{ 'btn-active': is('bold') }"
          @click="editor.chain().focus().toggleBold().run()"><b>B</b></button>
        <button type="button" class="btn btn-xs" :class="{ 'btn-active': is('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"><i>I</i></button>
        <template v-if="!inline">
          <button type="button" class="btn btn-xs" :class="{ 'btn-active': is('heading', { level: 3 }) }"
            @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">H</button>
          <button type="button" class="btn btn-xs" :class="{ 'btn-active': is('bulletList') }"
            @click="editor.chain().focus().toggleBulletList().run()">• List</button>
          <button type="button" class="btn btn-xs" :class="{ 'btn-active': is('link') }"
            @click="setLink">Link</button>
        </template>

        <div v-if="allowedTokens.length" class="dropdown" :class="{ 'dropdown-open': menuOpen }">
          <button type="button" class="btn btn-xs" @click="menuOpen = !menuOpen">Insert ▾</button>
          <ul v-if="menuOpen"
            class="dropdown-content menu bg-base-100 rounded-box shadow border border-base-300 z-10 w-56 p-1 mt-1">
            <li v-for="t in allowedTokens" :key="t">
              <button type="button" class="text-sm" @click="insertToken(t)">{{ chipLabel(t) }}</button>
            </li>
          </ul>
        </div>

        <button type="button" class="btn btn-ghost btn-xs ml-auto" @click="reset">Reset</button>
      </div>

      <!-- Deliberately bg-white in BOTH themes: this edits email CONTENT, which
           is always light-background HTML as the customer receives it
           (.email-prose pins its own dark-on-light text colours). -->
      <editor-content :editor="editor"
        class="border border-base-300 rounded-lg bg-white"
        :class="main ? 'p-4 max-h-[560px] overflow-y-auto' : (inline ? 'px-3 py-1.5' : 'p-3')" />
    </div>
  </div>
</template>

<style scoped>
.email-token-chip,
:deep(.email-token-chip) {
  display: inline-block;
  background: #e0ecff;
  color: #09457e;
  border: 1px solid #b9d4ff;
  border-radius: 9999px;
  padding: 0 8px;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 600;
  user-select: none;
  cursor: default;
  white-space: nowrap;
}
</style>
